import { useState, useEffect, useMemo } from 'react';
import { activityService } from '../../services/activityService';
import type { GroupAwareActivityResponse } from '../../types/activity';
import type { Product } from '../../types/product';
import type { Contact } from '../../types/contact';
import type { Stock } from '../../types/stock';
import type { ActivityType } from '../../types/activityType';

interface FilterData {
  products: Product[];
  clients: Contact[];
  stocks: Stock[];
  providers: Contact[];
  activityTypes: ActivityType[];
  serialNumber: string;
  fromDate?: string;
  toDate?: string;
  showOnlyActive: boolean;
}

interface UseActivityListParams {
  companyId: number | null;
  page: number;
  pageSize: number;
  filterData: FilterData;
  onPageCountChange: (totalPages: number) => void;
  refreshKey?: number;
}

interface UseActivityListReturn {
  groupItems: GroupAwareActivityResponse[];
  loading: boolean;
  error: string | null;
}

export function useActivityList({
  companyId,
  page,
  pageSize,
  filterData,
  onPageCountChange,
  refreshKey,
}: UseActivityListParams): UseActivityListReturn {
  const [groupItems, setGroupItems] = useState<GroupAwareActivityResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const productIds = useMemo(
    () => filterData.products.map(p => p.id),
    [filterData.products]
  );

  const clientIds = useMemo(
    () => filterData.clients.map(c => c.id),
    [filterData.clients]
  );

  const stockIds = useMemo(
    () => filterData.stocks.map(s => s.id),
    [filterData.stocks]
  );

  const providerIds = useMemo(
    () => filterData.providers.map(p => p.id),
    [filterData.providers]
  );

  const activityTypeIds = useMemo(
    () => filterData.activityTypes.map(t => t.id),
    [filterData.activityTypes]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchActivities = async () => {
      if (!companyId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await activityService.searchActivities({
          companyId,
          page,
          size: pageSize,
          productIds: productIds.length > 0 ? productIds : undefined,
          clientIds: clientIds.length > 0 ? clientIds : undefined,
          stockIds: stockIds.length > 0 ? stockIds : undefined,
          providerIds: providerIds.length > 0 ? providerIds : undefined,
          activityTypeIds: activityTypeIds.length > 0 ? activityTypeIds : undefined,
          serialNumber: filterData.serialNumber || undefined,
          fromDate: filterData.fromDate,
          toDate: filterData.toDate,
          includeArchived: !filterData.showOnlyActive,
        });
        
        if (cancelled) return;
        setGroupItems(response.content);
        onPageCountChange(response.totalPages);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching activities:', err);
        setError('Не вдалося завантажити активності');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchActivities();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, page, pageSize, productIds, clientIds, stockIds, providerIds, activityTypeIds,
      filterData.serialNumber, filterData.fromDate, filterData.toDate, filterData.showOnlyActive, refreshKey]);

  return {
    groupItems,
    loading,
    error,
  };
}
